import * as yup from 'yup'

/**
 * Unit price measurement units for Shopify product variants
 */
export enum ShopifyUnitPriceMeasurementMeasuredUnit {
  CL = 'CL', // 100 centiliters equals 1 liter
  CM = 'CM', // 100 centimeters equals 1 meter
  FLOZ = 'FLOZ', // Imperial system unit of volume (U.S. customary unit)
  FT = 'FT', // 1 foot equals 12 inches
  FT2 = 'FT2', // Imperial system unit of area
  G = 'G', // Metric system unit of weight
  GAL = 'GAL', // 1 gallon equals 128 fluid ounces (U.S. customary unit)
  IN = 'IN', // Imperial system unit of length
  ITEM = 'ITEM', // 1 item, a unit of count
  KG = 'KG', // 1 kilogram equals 1000 grams
  L = 'L', // Metric system unit of volume
  LB = 'LB', // Imperial system unit of weight
  M = 'M', // Metric system unit of length
  M2 = 'M2', // Metric system unit of area
  M3 = 'M3', // 1 cubic meter equals 1000 liters
  MG = 'MG', // 1000 milligrams equals 1 gram
  ML = 'ML', // 1000 milliliters equals 1 liter
  MM = 'MM', // 1000 millimeters equals 1 meter
  OZ = 'OZ', // 16 ounces equals 1 pound
  PT = 'PT', // 1 pint equals 16 fluid ounces (U.S. customary unit)
  QT = 'QT', // 1 quart equals 32 fluid ounces (U.S. customary unit)
  UNKNOWN = 'UNKNOWN', // The unit of measurement is unknown
  YD = 'YD', // 1 yard equals 36 inches
}

/**
 * Array of unit price measurement unit values for use in schemas
 */
export const SHOPIFY_UNIT_PRICE_MEASUREMENT_UNITS = Object.values(
  ShopifyUnitPriceMeasurementMeasuredUnit,
)

/**
 * Shopify product status values
 */
export enum ShopifyProductStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DRAFT = 'DRAFT',
}

/**
 * Shopify inventory update types
 */
export enum ShopifyInventoryUpdateType {
  SET = 'set',
  ADJUST = 'adjust',
  ITEM_UPDATE = 'itemUpdate',
}

/**
 * Shopify combined listing role values
 */
export enum ShopifyCombinedListingRole {
  CHILD = 'CHILD',
  PARENT = 'PARENT',
}

/**
 * Shopify media content types
 */
export enum ShopifyMediaContentType {
  EXTERNAL_VIDEO = 'EXTERNAL_VIDEO',
  IMAGE = 'IMAGE',
  MODEL_3D = 'MODEL_3D',
  VIDEO = 'VIDEO',
}

/**
 * Shopify product variant creation strategy
 */
export enum ShopifyProductVariantCreationStrategy {
  DEFAULT = 'DEFAULT',
  REMOVE_STANDALONE_VARIANT = 'REMOVE_STANDALONE_VARIANT',
}

/**
 * Shopify inventory policy values
 */
export enum ShopifyInventoryPolicy {
  CONTINUE = 'CONTINUE',
  DENY = 'DENY',
}

/**
 * Shopify weight unit values
 */
export enum ShopifyWeightUnit {
  GRAMS = 'GRAMS',
  KILOGRAMS = 'KILOGRAMS',
  OUNCES = 'OUNCES',
  POUNDS = 'POUNDS',
}

/**
 * Shopify country codes (ISO 3166-1 alpha-2)
 */
export enum ShopifyCountryCode {
  AC = 'AC', // Ascension Island
  AD = 'AD', // Andorra
  AE = 'AE', // United Arab Emirates
  AF = 'AF', // Afghanistan
  AG = 'AG', // Antigua & Barbuda
  AI = 'AI', // Anguilla
  AL = 'AL', // Albania
  AM = 'AM', // Armenia
  AN = 'AN', // Netherlands Antilles
  AO = 'AO', // Angola
  AR = 'AR', // Argentina
  AT = 'AT', // Austria
  AU = 'AU', // Australia
  AW = 'AW', // Aruba
  AX = 'AX', // Åland Islands
  AZ = 'AZ', // Azerbaijan
  BA = 'BA', // Bosnia & Herzegovina
  BB = 'BB', // Barbados
  BD = 'BD', // Bangladesh
  BE = 'BE', // Belgium
  BF = 'BF', // Burkina Faso
  BG = 'BG', // Bulgaria
  BH = 'BH', // Bahrain
  BI = 'BI', // Burundi
  BJ = 'BJ', // Benin
  BL = 'BL', // St. Barthélemy
  BM = 'BM', // Bermuda
  BN = 'BN', // Brunei
  BO = 'BO', // Bolivia
  BQ = 'BQ', // Caribbean Netherlands
  BR = 'BR', // Brazil
  BS = 'BS', // Bahamas
  BT = 'BT', // Bhutan
  BV = 'BV', // Bouvet Island
  BW = 'BW', // Botswana
  BY = 'BY', // Belarus
  BZ = 'BZ', // Belize
  CA = 'CA', // Canada
  CC = 'CC', // Cocos (Keeling) Islands
  CD = 'CD', // Congo - Kinshasa
  CF = 'CF', // Central African Republic
  CG = 'CG', // Congo - Brazzaville
  CH = 'CH', // Switzerland
  CI = 'CI', // Côte d'Ivoire
  CK = 'CK', // Cook Islands
  CL = 'CL', // Chile
  CM = 'CM', // Cameroon
  CN = 'CN', // China
  CO = 'CO', // Colombia
  CR = 'CR', // Costa Rica
  CU = 'CU', // Cuba
  CV = 'CV', // Cape Verde
  CW = 'CW', // Curaçao
  CX = 'CX', // Christmas Island
  CY = 'CY', // Cyprus
  CZ = 'CZ', // Czechia
  DE = 'DE', // Germany
  DJ = 'DJ', // Djibouti
  DK = 'DK', // Denmark
  DM = 'DM', // Dominica
  DO = 'DO', // Dominican Republic
  DZ = 'DZ', // Algeria
  EC = 'EC', // Ecuador
  EE = 'EE', // Estonia
  EG = 'EG', // Egypt
  EH = 'EH', // Western Sahara
  ER = 'ER', // Eritrea
  ES = 'ES', // Spain
  ET = 'ET', // Ethiopia
  FI = 'FI', // Finland
  FJ = 'FJ', // Fiji
  FK = 'FK', // Falkland Islands
  FO = 'FO', // Faroe Islands
  FR = 'FR', // France
  GA = 'GA', // Gabon
  GB = 'GB', // United Kingdom
  GD = 'GD', // Grenada
  GE = 'GE', // Georgia
  GF = 'GF', // French Guiana
  GG = 'GG', // Guernsey
  GH = 'GH', // Ghana
  GI = 'GI', // Gibraltar
  GL = 'GL', // Greenland
  GM = 'GM', // Gambia
  GN = 'GN', // Guinea
  GP = 'GP', // Guadeloupe
  GQ = 'GQ', // Equatorial Guinea
  GR = 'GR', // Greece
  GS = 'GS', // South Georgia & South Sandwich Islands
  GT = 'GT', // Guatemala
  GW = 'GW', // Guinea-Bissau
  GY = 'GY', // Guyana
  HK = 'HK', // Hong Kong SAR
  HM = 'HM', // Heard & McDonald Islands
  HN = 'HN', // Honduras
  HR = 'HR', // Croatia
  HT = 'HT', // Haiti
  HU = 'HU', // Hungary
  ID = 'ID', // Indonesia
  IE = 'IE', // Ireland
  IL = 'IL', // Israel
  IM = 'IM', // Isle of Man
  IN = 'IN', // India
  IO = 'IO', // British Indian Ocean Territory
  IQ = 'IQ', // Iraq
  IR = 'IR', // Iran
  IS = 'IS', // Iceland
  IT = 'IT', // Italy
  JE = 'JE', // Jersey
  JM = 'JM', // Jamaica
  JO = 'JO', // Jordan
  JP = 'JP', // Japan
  KE = 'KE', // Kenya
  KG = 'KG', // Kyrgyzstan
  KH = 'KH', // Cambodia
  KI = 'KI', // Kiribati
  KM = 'KM', // Comoros
  KN = 'KN', // St. Kitts & Nevis
  KP = 'KP', // North Korea
  KR = 'KR', // South Korea
  KW = 'KW', // Kuwait
  KY = 'KY', // Cayman Islands
  KZ = 'KZ', // Kazakhstan
  LA = 'LA', // Laos
  LB = 'LB', // Lebanon
  LC = 'LC', // St. Lucia
  LI = 'LI', // Liechtenstein
  LK = 'LK', // Sri Lanka
  LR = 'LR', // Liberia
  LS = 'LS', // Lesotho
  LT = 'LT', // Lithuania
  LU = 'LU', // Luxembourg
  LV = 'LV', // Latvia
  LY = 'LY', // Libya
  MA = 'MA', // Morocco
  MC = 'MC', // Monaco
  MD = 'MD', // Moldova
  ME = 'ME', // Montenegro
  MF = 'MF', // St. Martin
  MG = 'MG', // Madagascar
  MK = 'MK', // North Macedonia
  ML = 'ML', // Mali
  MM = 'MM', // Myanmar (Burma)
  MN = 'MN', // Mongolia
  MO = 'MO', // Macao SAR
  MQ = 'MQ', // Martinique
  MR = 'MR', // Mauritania
  MS = 'MS', // Montserrat
  MT = 'MT', // Malta
  MU = 'MU', // Mauritius
  MV = 'MV', // Maldives
  MW = 'MW', // Malawi
  MX = 'MX', // Mexico
  MY = 'MY', // Malaysia
  MZ = 'MZ', // Mozambique
  NA = 'NA', // Namibia
  NC = 'NC', // New Caledonia
  NE = 'NE', // Niger
  NF = 'NF', // Norfolk Island
  NG = 'NG', // Nigeria
  NI = 'NI', // Nicaragua
  NL = 'NL', // Netherlands
  NO = 'NO', // Norway
  NP = 'NP', // Nepal
  NR = 'NR', // Nauru
  NU = 'NU', // Niue
  NZ = 'NZ', // New Zealand
  OM = 'OM', // Oman
  PA = 'PA', // Panama
  PE = 'PE', // Peru
  PF = 'PF', // French Polynesia
  PG = 'PG', // Papua New Guinea
  PH = 'PH', // Philippines
  PK = 'PK', // Pakistan
  PL = 'PL', // Poland
  PM = 'PM', // St. Pierre & Miquelon
  PN = 'PN', // Pitcairn Islands
  PS = 'PS', // Palestinian Territories
  PT = 'PT', // Portugal
  PY = 'PY', // Paraguay
  QA = 'QA', // Qatar
  RE = 'RE', // Réunion
  RO = 'RO', // Romania
  RS = 'RS', // Serbia
  RU = 'RU', // Russia
  RW = 'RW', // Rwanda
  SA = 'SA', // Saudi Arabia
  SB = 'SB', // Solomon Islands
  SC = 'SC', // Seychelles
  SD = 'SD', // Sudan
  SE = 'SE', // Sweden
  SG = 'SG', // Singapore
  SH = 'SH', // St. Helena
  SI = 'SI', // Slovenia
  SJ = 'SJ', // Svalbard & Jan Mayen
  SK = 'SK', // Slovakia
  SL = 'SL', // Sierra Leone
  SM = 'SM', // San Marino
  SN = 'SN', // Senegal
  SO = 'SO', // Somalia
  SR = 'SR', // Suriname
  SS = 'SS', // South Sudan
  ST = 'ST', // São Tomé & Príncipe
  SV = 'SV', // El Salvador
  SX = 'SX', // Sint Maarten
  SY = 'SY', // Syria
  SZ = 'SZ', // Eswatini
  TA = 'TA', // Tristan da Cunha
  TC = 'TC', // Turks & Caicos Islands
  TD = 'TD', // Chad
  TF = 'TF', // French Southern Territories
  TG = 'TG', // Togo
  TH = 'TH', // Thailand
  TJ = 'TJ', // Tajikistan
  TK = 'TK', // Tokelau
  TL = 'TL', // Timor-Leste
  TM = 'TM', // Turkmenistan
  TN = 'TN', // Tunisia
  TO = 'TO', // Tonga
  TR = 'TR', // Türkiye
  TT = 'TT', // Trinidad & Tobago
  TV = 'TV', // Tuvalu
  TW = 'TW', // Taiwan
  TZ = 'TZ', // Tanzania
  UA = 'UA', // Ukraine
  UG = 'UG', // Uganda
  UM = 'UM', // U.S. Outlying Islands
  US = 'US', // United States
  UY = 'UY', // Uruguay
  UZ = 'UZ', // Uzbekistan
  VA = 'VA', // Vatican City
  VC = 'VC', // St. Vincent & Grenadines
  VE = 'VE', // Venezuela
  VG = 'VG', // British Virgin Islands
  VN = 'VN', // Vietnam
  VU = 'VU', // Vanuatu
  WF = 'WF', // Wallis & Futuna
  WS = 'WS', // Samoa
  XK = 'XK', // Kosovo
  YE = 'YE', // Yemen
  YT = 'YT', // Mayotte
  ZA = 'ZA', // South Africa
  ZM = 'ZM', // Zambia
  ZW = 'ZW', // Zimbabwe
  ZZ = 'ZZ', // Unknown Region
}

/**
 * Arrays of enum values for use in schemas
 */
export const SHOPIFY_PRODUCT_STATUSES = Object.values(ShopifyProductStatus)
export const SHOPIFY_INVENTORY_UPDATE_TYPES = Object.values(ShopifyInventoryUpdateType)
export const SHOPIFY_COMBINED_LISTING_ROLES = Object.values(ShopifyCombinedListingRole)
export const SHOPIFY_MEDIA_CONTENT_TYPES = Object.values(ShopifyMediaContentType)
export const SHOPIFY_PRODUCT_VARIANT_CREATION_STRATEGIES = Object.values(
  ShopifyProductVariantCreationStrategy,
)
export const SHOPIFY_INVENTORY_POLICIES = Object.values(ShopifyInventoryPolicy)
export const SHOPIFY_WEIGHT_UNITS = Object.values(ShopifyWeightUnit)
export const SHOPIFY_COUNTRY_CODES = Object.values(ShopifyCountryCode)

/**
 * Admin API `DraftOrderSortKeys` for `draftOrders` connections (root and B2B).
 * @see https://shopify.dev/docs/api/admin-graphql/latest/enums/DraftOrderSortKeys
 */
export const SHOPIFY_DRAFT_ORDER_SORT_KEYS = [
  'CUSTOMER_NAME',
  'ID',
  'NUMBER',
  'RELEVANCE',
  'STATUS',
  'TOTAL_PRICE',
  'UPDATED_AT',
] as const

export const shopifyUpdateInventoryResponse = yup.object({
  output: yup.object({
    data: yup.object({
      inventorySetQuantities: yup.object({
        userErrors: yup.array().of(
          yup
            .object({
              field: yup.string().notRequired(),
              message: yup.string().notRequired(),
            })
            .strict(),
        ),
        inventoryAdjustmentGroup: yup
          .object({
            createdAt: yup.string().notRequired(),
            reason: yup.string().notRequired(),
            changes: yup.array().of(
              yup
                .object({
                  name: yup.string().notRequired(),
                  delta: yup.number().notRequired(),
                })
                .strict(),
            ),
          })
          .strict(),
      }),
    }),
  }),
})
export type ShopifyUpdateInventoryResponse = yup.InferType<typeof shopifyUpdateInventoryResponse>

export const shopifyAdjustInventoryResponse = yup.object({
  output: yup.object({
    data: yup.object({
      inventoryAdjustQuantities: yup.object({
        userErrors: yup
          .array()
          .of(
            yup
              .object({
                field: yup.string().nullable().notRequired(),
                message: yup.string().nullable().notRequired(),
              })
              .strict(),
          )
          .nullable()
          .notRequired(),
        inventoryAdjustmentGroup: yup
          .object({
            createdAt: yup.string().nullable().notRequired(),
            reason: yup.string().nullable().notRequired(),
            changes: yup
              .array()
              .of(
                yup
                  .object({
                    name: yup.string().nullable().notRequired(),
                    delta: yup.number().nullable().notRequired(),
                  })
                  .strict(),
              )
              .nullable()
              .notRequired(),
          })
          .strict()
          .nullable()
          .notRequired(),
      }),
    }),
  }),
})
export type ShopifyAdjustInventoryResponse = yup.InferType<typeof shopifyAdjustInventoryResponse>

export const shopifyItemUpdateInventoryResponse = yup.object({
  output: yup.object({
    data: yup.object({
      inventoryItemUpdate: yup.object({
        inventoryItem: yup.object({
          id: yup.string().required(),
          unitCost: yup
            .object({
              amount: yup.string().required(),
            })
            .required(),
        }),
        tracked: yup.boolean().required(),
        countryCodeOfOrigin: yup.string().nullable().notRequired(),
        provinceCodeOfOrigin: yup.string().nullable().notRequired(),
        harmonizedSystemCode: yup.string().nullable().notRequired(),
        countryHarmonizedSystemCodes: yup.object({
          edges: yup.array().of(
            yup.object({
              node: yup.object({
                harmonizedSystemCode: yup.string().required(),
                countryCode: yup.string().required(),
              }),
            }),
          ),
        }),
        userErrors: yup
          .array()
          .of(
            yup
              .object({
                message: yup.string().nullable().notRequired(),
              })
              .strict(),
          )
          .nullable()
          .notRequired(),
      }),
    }),
  }),
})

export type ShopifyItemUpdateInventoryResponse = yup.InferType<
  typeof shopifyItemUpdateInventoryResponse
>

// PRODUCT CREATE -----------------------------------------------------------------------------------------

export const shopifyProductCreateResponse = yup.object({
  data: yup.object({
    productCreate: yup.object({
      product: yup.object({
        id: yup.string().required(),
      }),
    }),
  }),
})

export type ShopifyProductCreateResponse = yup.InferType<typeof shopifyProductCreateResponse>

// PRICE LIST CREATE -----------------------------------------------------------------------------------------

export const shopifyPriceListCreateResponse = yup.object({
  data: yup.object({
    priceListCreate: yup.object({
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
      priceList: yup
        .object({
          id: yup.string().required(),
          name: yup.string().notRequired(),
          currency: yup.string().notRequired(),
          parent: yup
            .object({
              adjustment: yup
                .object({
                  type: yup.string().notRequired(),
                  value: yup.number().notRequired(),
                })
                .notRequired(),
            })
            .notRequired(),
        })
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyPriceListCreateResponse = yup.InferType<typeof shopifyPriceListCreateResponse>

// PRICE LIST UPDATE -----------------------------------------------------------------------------------------

export const shopifyPriceListUpdateResponse = yup.object({
  data: yup.object({
    priceListUpdate: yup.object({
      priceList: yup
        .object({
          id: yup.string().required(),
          parent: yup
            .object({
              adjustment: yup
                .object({
                  type: yup.string().notRequired(),
                  value: yup.number().notRequired(),
                })
                .notRequired(),
            })
            .notRequired(),
        })
        .nullable()
        .notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
              code: yup.string().notRequired(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyPriceListUpdateResponse = yup.InferType<typeof shopifyPriceListUpdateResponse>

// PRICE LIST FIXED PRICES ADD -----------------------------------------------------------------------------------------

const shopifyMoneyAmountResponse = yup.object({
  amount: yup.string().notRequired(),
  currencyCode: yup.string().notRequired(),
})

export const shopifyPriceListFixedPricesAddResponse = yup.object({
  data: yup.object({
    priceListFixedPricesAdd: yup.object({
      prices: yup
        .array()
        .of(
          yup.object({
            compareAtPrice: shopifyMoneyAmountResponse.notRequired(),
            price: shopifyMoneyAmountResponse.notRequired(),
          }),
        )
        .notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              code: yup.string().notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyPriceListFixedPricesAddResponse = yup.InferType<
  typeof shopifyPriceListFixedPricesAddResponse
>

// PRICE LIST FIXED PRICES DELETE -----------------------------------------------------------------------------------------

export const shopifyPriceListFixedPricesDeleteResponse = yup.object({
  data: yup.object({
    priceListFixedPricesDelete: yup.object({
      deletedFixedPriceVariantIds: yup.array().of(yup.string()).notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              code: yup.string().notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyPriceListFixedPricesDeleteResponse = yup.InferType<
  typeof shopifyPriceListFixedPricesDeleteResponse
>

// CUSTOMER SET -----------------------------------------------------------------------------------------

export const shopifyCustomerSetResponse = yup.object({
  data: yup.object({
    customerSet: yup.object({
      customer: yup.object({
        id: yup.string().required(),
        firstName: yup.string().nullable().notRequired(),
        lastName: yup.string().nullable().notRequired(),
        email: yup.string().nullable().notRequired(),
        phone: yup.string().nullable().notRequired(),
        numberOfOrders: yup.string().nullable().notRequired(),
        amountSpent: yup
          .object({
            amount: yup.string().nullable().notRequired(),
            currencyCode: yup.string().nullable().notRequired(),
          })
          .nullable()
          .notRequired(),
        createdAt: yup.string().nullable().notRequired(),
        updatedAt: yup.string().nullable().notRequired(),
        note: yup.string().nullable().notRequired(),
        verifiedEmail: yup.boolean().nullable().notRequired(),
        validEmailAddress: yup.boolean().nullable().notRequired(),
        tags: yup.array().of(yup.string()).nullable().notRequired(),
        lifetimeDuration: yup.string().nullable().notRequired(),
        defaultAddress: yup
          .object({
            formattedArea: yup.string().nullable().notRequired(),
            address1: yup.string().nullable().notRequired(),
          })
          .nullable()
          .notRequired(),
        addressesV2: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    address1: yup.string().nullable().notRequired(),
                    address2: yup.string().nullable().notRequired(),
                    city: yup.string().nullable().notRequired(),
                    province: yup.string().nullable().notRequired(),
                    zip: yup.string().nullable().notRequired(),
                    country: yup.string().nullable().notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        image: yup
          .object({
            src: yup.string().nullable().notRequired(),
          })
          .nullable()
          .notRequired(),
        canDelete: yup.boolean().nullable().notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).nullable().notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyCustomerSetResponse = yup.InferType<typeof shopifyCustomerSetResponse>

// COMPANY CREATE -----------------------------------------------------------------------------------------

export const shopifyCompanyCreateResponse = yup.object({
  data: yup.object({
    companyCreate: yup.object({
      company: yup.object({
        id: yup.string().required(),
        name: yup.string().nullable().notRequired(),
        externalId: yup.string().nullable().notRequired(),
        mainContact: yup
          .object({
            id: yup.string().nullable().notRequired(),
            customer: yup
              .object({
                id: yup.string().nullable().notRequired(),
                email: yup.string().nullable().notRequired(),
                firstName: yup.string().nullable().notRequired(),
                lastName: yup.string().nullable().notRequired(),
              })
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        contacts: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    customer: yup
                      .object({
                        email: yup.string().nullable().notRequired(),
                        firstName: yup.string().nullable().notRequired(),
                        lastName: yup.string().nullable().notRequired(),
                      })
                      .nullable()
                      .notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        contactRoles: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    name: yup.string().nullable().notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        locations: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    name: yup.string().nullable().notRequired(),
                    shippingAddress: yup
                      .object({
                        firstName: yup.string().nullable().notRequired(),
                        lastName: yup.string().nullable().notRequired(),
                        address1: yup.string().nullable().notRequired(),
                        city: yup.string().nullable().notRequired(),
                        province: yup.string().nullable().notRequired(),
                        zip: yup.string().nullable().notRequired(),
                        country: yup.string().nullable().notRequired(),
                      })
                      .nullable()
                      .notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).nullable().notRequired(),
              message: yup.string().required(),
              code: yup.string().nullable().notRequired(),
            })
            .strict(),
        )
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyCreateResponse = yup.InferType<typeof shopifyCompanyCreateResponse>

// COMPANY UPDATE -----------------------------------------------------------------------------------------

export const shopifyCompanyUpdateResponse = yup.object({
  data: yup.object({
    companyUpdate: yup.object({
      company: yup.object({
        id: yup.string().required(),
        name: yup.string().nullable().notRequired(),
        externalId: yup.string().nullable().notRequired(),
        mainContact: yup
          .object({
            id: yup.string().nullable().notRequired(),
            customer: yup
              .object({
                id: yup.string().nullable().notRequired(),
                email: yup.string().nullable().notRequired(),
                firstName: yup.string().nullable().notRequired(),
                lastName: yup.string().nullable().notRequired(),
              })
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        contacts: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    customer: yup
                      .object({
                        email: yup.string().nullable().notRequired(),
                        firstName: yup.string().nullable().notRequired(),
                        lastName: yup.string().nullable().notRequired(),
                      })
                      .nullable()
                      .notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        contactRoles: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    name: yup.string().nullable().notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
        locations: yup
          .object({
            edges: yup
              .array()
              .of(
                yup.object({
                  node: yup.object({
                    id: yup.string().nullable().notRequired(),
                    name: yup.string().nullable().notRequired(),
                    shippingAddress: yup
                      .object({
                        firstName: yup.string().nullable().notRequired(),
                        lastName: yup.string().nullable().notRequired(),
                        address1: yup.string().nullable().notRequired(),
                        city: yup.string().nullable().notRequired(),
                        province: yup.string().nullable().notRequired(),
                        zip: yup.string().nullable().notRequired(),
                        country: yup.string().nullable().notRequired(),
                      })
                      .nullable()
                      .notRequired(),
                  }),
                }),
              )
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).nullable().notRequired(),
              message: yup.string().required(),
              code: yup.string().nullable().notRequired(),
            })
            .strict(),
        )
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyUpdateResponse = yup.InferType<typeof shopifyCompanyUpdateResponse>

// COMPANY DELETE -----------------------------------------------------------------------------------------

export const shopifyCompanyDeleteResponse = yup.object({
  data: yup.object({
    companyDelete: yup.object({
      deletedCompanyId: yup.string().nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).nullable().notRequired(),
              message: yup.string().required(),
              code: yup.string().nullable().notRequired(),
            })
            .strict(),
        )
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyDeleteResponse = yup.InferType<typeof shopifyCompanyDeleteResponse>

// CATALOG CREATE -----------------------------------------------------------------------------------------

const shopifyCatalogSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().nullable().notRequired(),
  title: yup.string().nullable().notRequired(),
  priceList: yup
    .object({
      id: yup.string().nullable().notRequired(),
      currency: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
  publication: yup
    .object({
      id: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
})

export const shopifyCatalogCreateResponse = yup.object({
  data: yup.object({
    catalogCreate: yup.object({
      catalog: shopifyCatalogSchema.nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup.object({
            field: yup.array().of(yup.string()).nullable().notRequired(),
            message: yup.string().required(),
            code: yup.string().nullable().notRequired(),
          }),
        )
        .default([])
        .required(),
    }),
  }),
})

export type ShopifyCatalogCreateResponse = yup.InferType<typeof shopifyCatalogCreateResponse>

// CATALOG UPDATE -----------------------------------------------------------------------------------------

export const shopifyCatalogUpdateResponse = yup.object({
  data: yup.object({
    catalogUpdate: yup.object({
      catalog: shopifyCatalogSchema.nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup.object({
            field: yup.array().of(yup.string()).nullable().notRequired(),
            message: yup.string().required(),
            code: yup.string().nullable().notRequired(),
          }),
        )
        .default([])
        .required(),
    }),
  }),
})

export type ShopifyCatalogUpdateResponse = yup.InferType<typeof shopifyCatalogUpdateResponse>

// CATALOG DELETE -----------------------------------------------------------------------------------------

export const shopifyCatalogDeleteResponse = yup.object({
  data: yup.object({
    catalogDelete: yup.object({
      deletedId: yup.string().nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup.object({
            field: yup.array().of(yup.string()).nullable().notRequired(),
            message: yup.string().required(),
            code: yup.string().nullable().notRequired(),
          }),
        )
        .default([])
        .required(),
    }),
  }),
})

export type ShopifyCatalogDeleteResponse = yup.InferType<typeof shopifyCatalogDeleteResponse>

// COMPANY LOCATION CREATE -----------------------------------------------------------------------------------------

export const shopifyCompanyLocationCreateResponse = yup.object({
  data: yup.object({
    companyLocationCreate: yup.object({
      companyLocation: yup.object({
        id: yup.string().required(),
        name: yup.string().notRequired(),
        externalId: yup.string().notRequired(),
        note: yup.string().notRequired(),
        phone: yup.string().notRequired(),
        locale: yup.string().notRequired(),
        taxRegistrationId: yup.string().notRequired(),
        taxExempt: yup.boolean().notRequired(),
        taxExemptions: yup.array().of(yup.string()).notRequired(),
        billingSameAsShipping: yup.boolean().nullable().notRequired(),
        billingAddress: yup
          .object({
            address1: yup.string().notRequired(),
            address2: yup.string().notRequired(),
            city: yup.string().notRequired(),
            zip: yup.string().notRequired(),
            recipient: yup.string().notRequired(),
            firstName: yup.string().notRequired(),
            lastName: yup.string().notRequired(),
            phone: yup.string().notRequired(),
            zoneCode: yup.string().notRequired(),
            countryCodeV2: yup.string().notRequired(),
          })
          .notRequired(),
        shippingAddress: yup
          .object({
            address1: yup.string().notRequired(),
            address2: yup.string().notRequired(),
            city: yup.string().notRequired(),
            zip: yup.string().notRequired(),
            recipient: yup.string().notRequired(),
            firstName: yup.string().notRequired(),
            lastName: yup.string().notRequired(),
            phone: yup.string().notRequired(),
            zoneCode: yup.string().notRequired(),
            countryCodeV2: yup.string().notRequired(),
          })
          .notRequired(),
        buyerExperienceConfiguration: yup
          .object({
            checkoutToDraft: yup.boolean().notRequired(),
            editableShippingAddress: yup.boolean().notRequired(),
          })
          .notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
              code: yup.string().notRequired(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyLocationCreateResponse = yup.InferType<
  typeof shopifyCompanyLocationCreateResponse
>

// COMPANY LOCATION UPDATE -----------------------------------------------------------------------------------------

export const shopifyCompanyLocationUpdateResponse = yup.object({
  data: yup.object({
    companyLocationUpdate: yup.object({
      companyLocation: yup.object({
        id: yup.string().required(),
        name: yup.string().notRequired(),
        externalId: yup.string().notRequired(),
        note: yup.string().notRequired(),
        phone: yup.string().notRequired(),
        locale: yup.string().notRequired(),
        buyerExperienceConfiguration: yup
          .object({
            checkoutToDraft: yup.boolean().notRequired(),
            editableShippingAddress: yup.boolean().notRequired(),
          })
          .notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
              code: yup.string().notRequired(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyLocationUpdateResponse = yup.InferType<
  typeof shopifyCompanyLocationUpdateResponse
>

// COMPANY LOCATION TAX SETTINGS UPDATE -----------------------------------------------------------------------------------------

export const shopifyCompanyLocationTaxSettingsUpdateResponse = yup.object({
  data: yup.object({
    companyLocationTaxSettingsUpdate: yup.object({
      companyLocation: yup.object({
        id: yup.string().required(),
        taxSettings: yup
          .object({
            taxExempt: yup.boolean().notRequired(),
            taxRegistrationId: yup.string().nullable().notRequired(),
            taxExemptions: yup.array().of(yup.string()).notRequired(),
          })
          .notRequired(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
              code: yup.string().notRequired(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyLocationTaxSettingsUpdateResponse = yup.InferType<
  typeof shopifyCompanyLocationTaxSettingsUpdateResponse
>

// COMPANY LOCATION DELETE -----------------------------------------------------------------------------------------

export const shopifyCompanyLocationDeleteResponse = yup.object({
  data: yup.object({
    companyLocationDelete: yup.object({
      deletedCompanyLocationId: yup.string().nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).nullable().notRequired(),
              message: yup.string().required(),
              code: yup.string().nullable().notRequired(),
            })
            .strict(),
        )
        .nullable()
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyLocationDeleteResponse = yup.InferType<
  typeof shopifyCompanyLocationDeleteResponse
>

// COMPANY CONTACT CREATE -----------------------------------------------------------------------------------------

export const shopifyCompanyContactCreateResponse = yup.object({
  data: yup.object({
    companyContactCreate: yup.object({
      companyContact: yup.object({
        id: yup.string().required(),
        company: yup
          .object({
            id: yup.string().required(),
            name: yup.string().notRequired(),
          })
          .required(),
        customer: yup
          .object({
            id: yup.string().required(),
            firstName: yup.string().notRequired(),
            lastName: yup.string().notRequired(),
            email: yup.string().notRequired(),
          })
          .required(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
              code: yup.string().notRequired(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyContactCreateResponse = yup.InferType<
  typeof shopifyCompanyContactCreateResponse
>

// COMPANY CONTACT UPDATE -----------------------------------------------------------------------------------------

export const shopifyCompanyContactUpdateResponse = yup.object({
  data: yup.object({
    companyContactUpdate: yup.object({
      companyContact: yup.object({
        id: yup.string().required(),
        company: yup
          .object({
            id: yup.string().required(),
            name: yup.string().notRequired(),
          })
          .required(),
        customer: yup
          .object({
            id: yup.string().required(),
            firstName: yup.string().notRequired(),
            lastName: yup.string().notRequired(),
            email: yup.string().notRequired(),
          })
          .required(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyContactUpdateResponse = yup.InferType<
  typeof shopifyCompanyContactUpdateResponse
>

// COMPANY CONTACT DELETE -----------------------------------------------------------------------------------------

export const shopifyCompanyContactDeleteResponse = yup.object({
  data: yup.object({
    companyContactDelete: yup.object({
      deletedCompanyContactId: yup.string().nullable().notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyContactDeleteResponse = yup.InferType<
  typeof shopifyCompanyContactDeleteResponse
>

// COMPANY ASSIGN CUSTOMER AS CONTACT ---------------------------------------------------------------------------

export const shopifyCompanyAssignCustomerAsContactResponse = yup.object({
  data: yup.object({
    companyAssignCustomerAsContact: yup.object({
      companyContact: yup.object({
        id: yup.string().required(),
        company: yup
          .object({
            id: yup.string().required(),
            name: yup.string().notRequired(),
          })
          .required(),
        customer: yup
          .object({
            id: yup.string().required(),
            firstName: yup.string().notRequired(),
            lastName: yup.string().notRequired(),
            email: yup.string().notRequired(),
          })
          .required(),
      }),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyAssignCustomerAsContactResponse = yup.InferType<
  typeof shopifyCompanyAssignCustomerAsContactResponse
>

// COMPANY ASSIGN MAIN CONTACT ---------------------------------------------------------------------------

export const shopifyCompanyAssignMainContactResponse = yup.object({
  data: yup.object({
    companyAssignMainContact: yup.object({
      company: yup
        .object({
          id: yup.string().required(),
          name: yup.string().notRequired(),
          mainContact: yup
            .object({
              id: yup.string().required(),
              customer: yup
                .object({
                  id: yup.string().required(),
                  firstName: yup.string().notRequired(),
                  lastName: yup.string().notRequired(),
                  email: yup.string().notRequired(),
                })
                .notRequired(),
            })
            .notRequired(),
        })
        .notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyAssignMainContactResponse = yup.InferType<
  typeof shopifyCompanyAssignMainContactResponse
>

// COMPANY LOCATION ASSIGN ROLES -----------------------------------------------------------------------------------------

export const shopifyCompanyLocationAssignRolesResponse = yup.object({
  data: yup.object({
    companyLocationAssignRoles: yup.object({
      roleAssignments: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            company: yup
              .object({
                id: yup.string().required(),
                name: yup.string().notRequired(),
              })
              .required(),
            companyContact: yup
              .object({
                id: yup.string().required(),
                customer: yup
                  .object({
                    id: yup.string().notRequired(),
                    email: yup.string().notRequired(),
                  })
                  .notRequired(),
              })
              .required(),
            companyLocation: yup
              .object({
                id: yup.string().required(),
                name: yup.string().notRequired(),
                billingAddress: yup
                  .object({
                    address1: yup.string().notRequired(),
                    address2: yup.string().notRequired(),
                    city: yup.string().notRequired(),
                    zip: yup.string().notRequired(),
                    recipient: yup.string().notRequired(),
                    firstName: yup.string().notRequired(),
                    lastName: yup.string().notRequired(),
                    phone: yup.string().notRequired(),
                    zoneCode: yup.string().notRequired(),
                  })
                  .notRequired(),
              })
              .required(),
            role: yup
              .object({
                id: yup.string().required(),
                name: yup.string().notRequired(),
                note: yup.string().notRequired().nullable(),
              })
              .required(),
          }),
        )
        .notRequired(),
      userErrors: yup
        .array()
        .of(
          yup
            .object({
              field: yup.array().of(yup.string()).notRequired(),
              message: yup.string().required(),
            })
            .strict(),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyCompanyLocationAssignRolesResponse = yup.InferType<
  typeof shopifyCompanyLocationAssignRolesResponse
>

// PRODUCT UPDATE -----------------------------------------------------------------------------------------

export const shopifyProductUpdateResponse = yup.object({
  data: yup.object({
    productUpdate: yup.object({
      product: yup.object({
        id: yup.string().required(),
      }),
    }),
  }),
})

export type ShopifyProductUpdateResponse = yup.InferType<typeof shopifyProductUpdateResponse>

// PRODUCT VARIANT BULK CREATE -----------------------------------------------------------------------------------------

export const shopifyProductVariantBulkCreateResponse = yup.object({
  data: yup.object({
    productVariantsBulkCreate: yup.object({
      productVariants: yup.array().of(
        yup.object({
          id: yup.string().required(),
          title: yup.string().notRequired(),
          sku: yup.string().notRequired(),
          price: yup.string().notRequired(),
          compareAtPrice: yup.string().notRequired(),
          barcode: yup.string().notRequired(),
          inventoryItem: yup
            .object({
              id: yup.string().notRequired(),
              cost: yup.string().notRequired(),
              countryCodeOfOrigin: yup.string().notRequired(),
              countryHarmonizedSystemCodes: yup.array().of(
                yup
                  .object({
                    harmonizedSystemCode: yup.string().notRequired(),
                    countryCode: yup.string().notRequired(),
                  })
                  .strict(),
              ),
              harmonizedSystemCode: yup.string().notRequired(),
              measurement: yup
                .object({
                  weight: yup
                    .object({
                      unit: yup.string().notRequired(),
                      value: yup.number().notRequired(),
                    })
                    .strict()
                    .notRequired(),
                })
                .strict()
                .notRequired(),
            })
            .strict(),
        }),
      ),
    }),
  }),
})

export type ShopifyProductVariantBulkCreateResponse = yup.InferType<
  typeof shopifyProductVariantBulkCreateResponse
>

// PRODUCT VARIANT BULK UPDATE -----------------------------------------------------------------------------------------

export const shopifyProductVariantBulkUpdateResponse = yup.object({
  data: yup.object({
    productVariantsBulkUpdate: yup.object({
      productVariants: yup.array().of(
        yup.object({
          id: yup.string().required(),
          title: yup.string().notRequired(),
          sku: yup.string().notRequired(),
          price: yup.string().notRequired(),
          compareAtPrice: yup.string().notRequired(),
          barcode: yup.string().notRequired(),
          inventoryItem: yup
            .object({
              id: yup.string().notRequired(),
              cost: yup.string().notRequired(),
              countryCodeOfOrigin: yup.string().notRequired(),
              countryHarmonizedSystemCodes: yup.array().of(
                yup
                  .object({
                    harmonizedSystemCode: yup.string().notRequired(),
                    countryCode: yup.string().notRequired(),
                  })
                  .strict(),
              ),
              harmonizedSystemCode: yup.string().notRequired(),
              measurement: yup
                .object({
                  weight: yup
                    .object({
                      unit: yup.string().notRequired(),
                      value: yup.number().notRequired(),
                    })
                    .strict()
                    .notRequired(),
                })
                .strict()
                .notRequired(),
            })
            .strict(),
        }),
      ),
    }),
  }),
})

export type ShopifyProductVariantBulkUpdateResponse = yup.InferType<
  typeof shopifyProductVariantBulkUpdateResponse
>

const OrderSchema = yup.object({
  id: yup.number().required(),
  contact_email: yup.string().email().notRequired(),
  created_at: yup.string().required(),
  currency: yup.string().required(),
  current_total_price: yup.number().required(),

  customer: yup
    .object({
      id: yup.number().required(),
      email: yup.string().email().notRequired(),
      tags: yup.string().notRequired(),
    })
    .notRequired(),

  confirmed: yup.boolean().notRequired(),
  cancel_reason: yup.string().nullable().notRequired(),
  cancelled_at: yup.string().nullable().notRequired(),
  checkout_id: yup.number().nullable().notRequired(),
  closed_at: yup.string().nullable().notRequired(),
  confirmation_number: yup.string().nullable().notRequired(),
  current_subtotal_price: yup.number().notRequired(),
  current_total_discounts: yup.number().notRequired(),
  current_total_tax: yup.number().notRequired(),
  customer_locale: yup.string().notRequired(),
  discount_codes: yup
    .array()
    .of(
      yup
        .object({
          code: yup.string().notRequired(),
          amount: yup.number().notRequired(),
          type: yup.string().notRequired(),
        })
        .strict(),
    )
    .notRequired(),
  email: yup.string().email().notRequired(),
  estimated_taxes: yup.boolean().notRequired(),
  financial_status: yup.string().notRequired(),
  fulfillment_status: yup.string().nullable().notRequired(),
  name: yup.string().notRequired(),
  note: yup.string().nullable().notRequired(),
  number: yup.number().notRequired(),
  order_number: yup.number().notRequired(),
  order_status_url: yup.string().url().notRequired(),
  phone: yup.string().nullable().notRequired(),
  po_number: yup.string().nullable().notRequired(),
  processed_at: yup.string().nullable().notRequired(),
  source_identifier: yup.string().nullable().notRequired(),
  source_name: yup.string().notRequired(),
  source_url: yup.string().url().nullable().notRequired(),
  subtotal_price: yup.number().notRequired(),
  tags: yup.string().notRequired(),
  tax_exempt: yup.boolean().notRequired(),
  taxes_included: yup.boolean().notRequired(),
  total_discounts: yup.number().notRequired(),
  total_line_items_price: yup.number().notRequired(),
  total_outstanding: yup.number().notRequired(),
  total_price: yup.number().notRequired(),
  total_tax: yup.number().notRequired(),
  total_tip_received: yup.number().notRequired(),
  total_weight: yup.number().notRequired(),
  updated_at: yup.string().notRequired(),

  billing_address: yup.mixed().notRequired(),
  shipping_address: yup.mixed().notRequired(),
  line_items: yup.mixed().notRequired(),
  shipping_lines: yup.mixed().notRequired(),
  fulfillments: yup.mixed().notRequired(),
})

export const ordersCreateSchema = OrderSchema

export const ordersUpdatedSchema = OrderSchema

export const ordersCancelledSchema = OrderSchema

export const ordersPaidSchema = OrderSchema

export const ordersDeleteSchema = yup.object({
  id: yup.number().required(),
})

export const inventoryItemCreateSchema = yup.object({
  id: yup.number().required(),
  sku: yup.string().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  requires_shipping: yup.boolean().notRequired(),
  cost: yup.number().nullable().notRequired(),
  country_code_of_origin: yup.string().nullable().notRequired(),
  tracked: yup.boolean().notRequired(),
})

export const inventoryItemUpdateSchema = yup.object({
  id: yup.number().required(),
  sku: yup.string().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  requires_shipping: yup.boolean().notRequired(),
  cost: yup.number().nullable().notRequired(),
  country_code_of_origin: yup.string().nullable().notRequired(),
  tracked: yup.boolean().notRequired(),
})

export const inventoryItemDeleteSchema = yup.object({
  id: yup.number().required(),
  sku: yup.string().notRequired(),
})

export const inventoryLevelsUpdateSchema = yup.object({
  inventory_item_id: yup.number().required(),
  location_id: yup.number().required(),
  available: yup.number().nullable().notRequired(),
  updated_at: yup.string().notRequired(),
  admin_graphql_api_id: yup.string().required(),
})

export const inventoryLevelsConnectSchema = yup.object({
  inventory_item_id: yup.number().required(),
  location_id: yup.number().required(),
  available: yup.number().nullable().notRequired(),
  updated_at: yup.string().notRequired(),
  admin_graphql_api_id: yup.string().required(),
})

export const inventoryLevelsDisconnectSchema = yup.object({
  inventory_item_id: yup.number().required(),
  location_id: yup.number().required(),
})

export const locationsUpdateSchema = yup.object({
  id: yup.number().required(),
  name: yup.string().notRequired(),
  address1: yup.string().notRequired(),
  address2: yup.string().notRequired(),
  city: yup.string().notRequired(),
  zip: yup.string().notRequired(),
  province: yup.string().notRequired(),
  country: yup.string().notRequired(),
  phone: yup.string().nullable().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  country_code: yup.string().notRequired(),
  country_name: yup.string().notRequired(),
  province_code: yup.string().notRequired(),
  legacy: yup.boolean().notRequired(),
  active: yup.boolean().notRequired(),
  admin_graphql_api_id: yup.string().required(),
})

export const locationsCreateSchema = locationsUpdateSchema

export const locationsDeleteSchema = yup.object({
  id: yup.number().required(),
})

export const companiesUpdateSchema = yup.object({
  name: yup.string().notRequired(),
  note: yup.string().notRequired(),
  external_id: yup.string().notRequired(),
  main_contact_admin_graphql_api_id: yup.string().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  customer_since: yup.string().notRequired(),
  admin_graphql_api_id: yup.string().required(),
})

export const companiesCreateSchema = companiesUpdateSchema

export const companiesDeleteSchema = companiesUpdateSchema

/** Nested company on company_contacts/create, company_contacts/update, and company_contacts/delete payloads. */
const companyContactWebhookCompanySchema = yup.object({
  name: yup.string().notRequired(),
  note: yup.string().notRequired(),
  external_id: yup.string().notRequired(),
  main_contact_admin_graphql_api_id: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  customer_since: yup.string().notRequired(),
  admin_graphql_api_id: yup.string().required(),
})

export const companyContactsUpdateSchema = yup.object({
  customer_admin_graphql_api_id: yup.string().required(),
  title: yup.string().notRequired(),
  locale: yup.string().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  admin_graphql_api_id: yup.string().required(),
  company: companyContactWebhookCompanySchema.required(),
})

export const companyContactsCreateSchema = companyContactsUpdateSchema

/** company_contacts/delete — nullable fields when Shopify omits customer/company data. */
const companyContactWebhookCompanyDeleteSchema = yup.object({
  name: yup.string().nullable().notRequired(),
  note: yup.string().nullable().notRequired(),
  external_id: yup.string().nullable().notRequired(),
  main_contact_admin_graphql_api_id: yup.string().nullable().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  updated_at: yup.string().nullable().notRequired(),
  customer_since: yup.string().nullable().notRequired(),
  admin_graphql_api_id: yup.string().nullable().notRequired(),
})

export const companyContactsDeleteSchema = yup.object({
  customer_admin_graphql_api_id: yup.string().nullable().notRequired(),
  title: yup.string().nullable().notRequired(),
  locale: yup.string().nullable().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  updated_at: yup.string().nullable().notRequired(),
  admin_graphql_api_id: yup.string().required(),
  company: companyContactWebhookCompanyDeleteSchema.nullable().notRequired(),
})

export const customerCreateSchema = yup.object({
  id: yup.number().required(),
  email: yup.string().email().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  first_name: yup.string().notRequired(),
  last_name: yup.string().notRequired(),
  orders_count: yup.number().notRequired(),
  state: yup.string().notRequired(),
  total_spent: yup.number().notRequired(),
  last_order_id: yup.number().nullable().notRequired(),
  last_order_name: yup.string().nullable().notRequired(),
  note: yup.string().notRequired(),
  currency: yup.string().notRequired(),
  phone: yup.string().nullable().notRequired(),
  addresses: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        customer_id: yup.number().required(),
        first_name: yup.string().notRequired(),
        last_name: yup.string().notRequired(),
        company: yup.string().notRequired(),
        address1: yup.string().notRequired(),
        address2: yup.string().notRequired(),
        city: yup.string().notRequired(),
        province: yup.string().notRequired(),
        country: yup.string().notRequired(),
        zip: yup.string().notRequired(),
        phone: yup.string().notRequired(),
        name: yup.string().notRequired(),
        province_code: yup.string().notRequired(),
        country_code: yup.string().notRequired(),
        country_name: yup.string().notRequired(),
        default: yup.boolean().notRequired(),
      }),
    )

    .notRequired(),
})

export const customerUpdateSchema = yup.object({
  id: yup.number().required(),
  email: yup.string().email().notRequired(),
  created_at: yup.string().required(),
  updated_at: yup.string().notRequired(),
  first_name: yup.string().notRequired(),
  last_name: yup.string().notRequired(),
  orders_count: yup.number().notRequired(),
  state: yup.string().notRequired(),
  total_spent: yup.number().notRequired(),
  last_order_id: yup.number().nullable().notRequired(),
  last_order_name: yup.string().nullable().notRequired(),
  note: yup.string().notRequired(),
  currency: yup.string().notRequired(),
  phone: yup.string().nullable().notRequired(),
  addresses: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        customer_id: yup.number().required(),
        first_name: yup.string().notRequired(),
        last_name: yup.string().notRequired(),
        company: yup.string().notRequired(),
        address1: yup.string().notRequired(),
        address2: yup.string().notRequired(),
        city: yup.string().notRequired(),
        province: yup.string().notRequired(),
        country: yup.string().notRequired(),
        zip: yup.string().notRequired(),
        phone: yup.string().notRequired(),
        name: yup.string().notRequired(),
        province_code: yup.string().notRequired(),
        country_code: yup.string().notRequired(),
        country_name: yup.string().notRequired(),
        default: yup.boolean().notRequired(),
      }),
    )

    .notRequired(),
})

export const customerDeleteSchema = yup.object({
  id: yup.number().required(),
})

export const productsCreateSchema = yup.object({
  body_html: yup.string().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  handle: yup.string().required(),
  id: yup.number().required(),
  product_type: yup.string().notRequired(),
  published_at: yup.string().notRequired(),
  title: yup.string().required(),
  updated_at: yup.string().notRequired(),
  vendor: yup.string().notRequired(),
  status: yup.string().notRequired(),
  variants: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        title: yup.string().required(),
        price: yup.number().required(),
        sku: yup.string().notRequired(),
        inventory_quantity: yup.number().notRequired(),
        weight: yup.number().notRequired(),
        weight_unit: yup.string().notRequired(),
        option1: yup.string().notRequired(),
        option2: yup.string().notRequired(),
        option3: yup.string().notRequired(),
      }),
    )

    .notRequired(),
})

export const productsUpdateSchema = yup.object({
  body_html: yup.string().notRequired(),
  created_at: yup.string().nullable().notRequired(),
  handle: yup.string().required(),
  id: yup.number().required(),
  product_type: yup.string().notRequired(),
  published_at: yup.string().notRequired(),
  title: yup.string().required(),
  updated_at: yup.string().notRequired(),
  vendor: yup.string().notRequired(),
  status: yup.string().notRequired(),
  variants: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        title: yup.string().required(),
        price: yup.number().required(),
        sku: yup.string().notRequired(),
        inventory_quantity: yup.number().notRequired(),
        weight: yup.number().notRequired(),
        weight_unit: yup.string().notRequired(),
        option1: yup.string().notRequired(),
        option2: yup.string().notRequired(),
        option3: yup.string().notRequired(),
      }),
    )
    .notRequired(),
})

export const productsDeleteSchema = yup.object({
  id: yup.number().required(),
})

export const returnsUpdateSchema = yup.object({
  admin_graphql_api_id: yup.string().required(),
  return_line_items: yup
    .object({
      removals: yup
        .array()
        .of(
          yup.object({
            admin_graphql_api_id: yup.string().required(),
            delta: yup.number().required(),
          }),
        )
        .required(),
    })
    .required(),
  restocking_fees: yup
    .object({
      updates: yup.array().of(yup.object()).notRequired(),
      removals: yup.array().of(yup.object()).notRequired(),
    })
    .required(),
  return_shipping_fees: yup
    .object({
      updates: yup.array().of(yup.object()).notRequired(),
      removals: yup.array().of(yup.object()).notRequired(),
    })
    .notRequired(),
})

// SHOPIFY FULFILLMENT ORDERS SCHEMA -------------------------------------------------------------

export const shopifyFulfillmentCreateResponse = yup.object({
  data: yup.object({
    fulfillmentCreate: yup.object({
      fulfillment: yup.object({
        id: yup.string().required(),
      }),
    }),
  }),
})

export type ShopifyFulfillmentCreateResponse = yup.InferType<
  typeof shopifyFulfillmentCreateResponse
>

export const shopifyFulfillmentTrackingInfoUpdateResponse = yup.object({
  data: yup.object({
    fulfillmentTrackingInfoUpdate: yup.object({
      fulfillment: yup.object({
        id: yup.string().required(),
        status: yup.string().required(),
        trackingInfo: yup.array().of(
          yup
            .object({
              company: yup.string().nullable(),
              number: yup.string().nullable(),
              url: yup.string().nullable(),
            })
            .strict(),
        ),
      }),
      userErrors: yup
        .array()
        .of(
          yup.object({
            field: yup.array().of(yup.string()).nullable(),
            message: yup.string().required(),
          }),
        )
        .notRequired(),
    }),
  }),
})

export type ShopifyFulfillmentTrackingInfoUpdateResponse = yup.InferType<
  typeof shopifyFulfillmentTrackingInfoUpdateResponse
>

/** Schema for creating a fulfillment in Shopify */
export const createFulfillmentSchema = yup.object({
  fulfillment: yup
    .object({
      trackingInfo: yup
        .object({
          number: yup.string().notRequired(),
          url: yup.string().url().notRequired(),
          numbers: yup.array(yup.string().required()).notRequired(),
          urls: yup.array(yup.string().url('Must be a valid URL').required()).notRequired(),
        })
        .strict()
        .notRequired(),
      notifyCustomer: yup.boolean().notRequired(),
      lineItemsByFulfillmentOrder: yup
        .object({
          fulfillmentOrderId: yup.string().required(),
          fulfillmentOrderLineItems: yup.array().of(
            yup.object({
              id: yup.string().required(),
              quantity: yup.number().required(),
            }),
          ),
        })
        .required(),
      originAddress: yup
        .object({
          address1: yup.string().notRequired(),
          address2: yup.string().notRequired(),
          city: yup.string().notRequired(),
          countryCode: yup.string().required(),
          provinceCode: yup.string().notRequired(),
          zip: yup.string().notRequired(),
        })
        .notRequired(),
    })
    .required(),
  message: yup.string().notRequired(),
})

// PageInfo schema for pagination in Shopify GraphQL API
const pageInfoSchema = yup
  .object({
    hasNextPage: yup.boolean().optional().default(false),
    endCursor: yup.string().nullable().optional(),
  })
  .strict()

/**
 * Admin GraphQL FulfillmentOrderSupportedAction.
 * @see https://shopify.dev/docs/api/admin-graphql/latest/objects/FulfillmentOrderSupportedAction
 */
export const shopifyFulfillmentOrderSupportedActionSchema = yup.object({
  action: yup.string().required(),
  externalUrl: yup.string().nullable().optional(),
})

/**
 * Assigned location fields used by Order.fulfillmentOrders list reads.
 * (Single-FO read includes richer location.fulfillmentService — kept on that schema.)
 */
export const shopifyFulfillmentOrderListAssignedLocationSchema = yup
  .object({
    name: yup.string().nullable().optional(),
    address1: yup.string().nullable().optional(),
    address2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    province: yup.string().nullable().optional(),
    countryCode: yup.string().nullable().optional(),
    zip: yup.string().nullable().optional(),
    phone: yup.string().nullable().optional(),
    location: yup
      .object({
        id: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .required()

/** FO line item node on Order.fulfillmentOrders / more-line-items pagination. */
export const shopifyFulfillmentOrderListLineItemNodeSchema = yup.object({
  id: yup.string().required(),
  remainingQuantity: yup.number().required(),
  totalQuantity: yup.number().required(),
  lineItem: yup
    .object({
      id: yup.string().required(),
    })
    .nullable()
    .optional(),
})

/**
 * Flattened FO used as Order.fulfillmentOrders list output (and GraphQL edge node, minus
 * nested lineItems connection shape).
 */
export const shopifyFulfillmentOrderListNodeFieldsSchema = {
  id: yup.string().required(),
  createdAt: yup.string().required(),
  fulfillAt: yup.string().nullable(),
  fulfillBy: yup.string().nullable(),
  requestStatus: yup.string().required(),
  status: yup.string().required(),
  assignedLocation: shopifyFulfillmentOrderListAssignedLocationSchema,
  supportedActions: yup.array().of(shopifyFulfillmentOrderSupportedActionSchema).required(),
}

const shopifyFulfillmentOrderLineItemsConnectionSchema = yup
  .object({
    edges: yup
      .array()
      .of(
        yup.object({
          node: shopifyFulfillmentOrderListLineItemNodeSchema,
        }),
      )
      .default([])
      .required(),
    pageInfo: pageInfoSchema,
  })
  .required()

/**
 * Slim more-line-items GraphQL payload (`data.fulfillmentOrder.lineItems`).
 * Used by both list and single FO pagination — does not require full FO fields.
 */
export const shopifyMoreFulfillmentOrderLineItemsDataSchema = yup.object({
  fulfillmentOrder: yup
    .object({
      id: yup.string().optional(),
      lineItems: shopifyFulfillmentOrderLineItemsConnectionSchema,
    })
    .required(),
})

export const shopifyMoreFulfillmentOrderLineItemsSchema = yup.object({
  data: shopifyMoreFulfillmentOrderLineItemsDataSchema.required(),
})

// Fulfillment Order edge node for Order.fulfillmentOrders GraphQL responses
const fulfillmentOrderSchema = yup.object({
  ...shopifyFulfillmentOrderListNodeFieldsSchema,
  lineItems: shopifyFulfillmentOrderLineItemsConnectionSchema,
})

// Shopify Order Response schema from the GraphQL API
export const shopifyOrderSchema = yup.object({
  order: yup.object({
    fulfillmentOrders: yup.object({
      edges: yup
        .array()
        .of(
          yup.object({
            cursor: yup.string().required(),
            node: fulfillmentOrderSchema,
          }),
        )
        .default([])
        .required(),
      pageInfo: pageInfoSchema,
    }),
  }),
})

export type ShopifyOrder = yup.InferType<typeof shopifyOrderSchema>

export const returnsCloseSchema = yup.object({
  id: yup.number().required(),
  admin_graphql_api_id: yup.string().required(),
  order_id: yup.number().required(),
  status: yup.string().required(),
})

// returns/request webhook schema
export const returnsRequestSchema = yup.object({
  id: yup.string().required(),
  admin_graphql_api_id: yup.string().required(),
  status: yup.string().required(),
  order: yup
    .object({
      id: yup.string().required(),
      admin_graphql_api_id: yup.string().required(),
    })
    .required(),
  total_return_line_items: yup.number().required(),
  name: yup.string().nullable().notRequired(),
  return_line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        admin_graphql_api_id: yup.string().required(),
        fulfillment_line_item: yup
          .object({
            id: yup.number().required(),
            admin_graphql_api_id: yup.string().required(),
            line_item: yup
              .object({
                id: yup.number().required(),
                admin_graphql_api_id: yup.string().required(),
              })
              .required(),
          })
          .required(),
        quantity: yup.number().required(),
        return_reason: yup.string().required(),
        return_reason_note: yup.string().nullable().notRequired(),
        customer_note: yup.string().nullable().notRequired(),
      }),
    )
    .required(),
  return_shipping_fees: yup
    .array()
    .of(
      yup
        .object({
          amount: yup.number().notRequired(),
          currency: yup.string().notRequired(),
          tax_amount: yup.number().notRequired(),
          tax_currency: yup.string().notRequired(),
          description: yup.string().notRequired(),
        })
        .notRequired(),
    )
    .notRequired(),
  exchange_line_items: yup
    .array()
    .of(
      yup
        .object({
          id: yup.number().notRequired(),
          admin_graphql_api_id: yup.string().notRequired(),
          variant_id: yup.number().notRequired(),
          quantity: yup.number().notRequired(),
          title: yup.string().notRequired(),
          variant_title: yup.string().notRequired(),
          sku: yup.string().notRequired(),
          requires_shipping: yup.boolean().notRequired(),
        })
        .notRequired(),
    )
    .required(),
  total_exchange_line_items: yup.number().required(),
})

// returns/approve webhook schema
export const returnsApproveSchema = yup.object({
  id: yup.number().required(),
  admin_graphql_api_id: yup.string().required(),
  status: yup.string().required(),
  order: yup
    .object({
      id: yup.number().required(),
      admin_graphql_api_id: yup.string().required(),
    })
    .required(),
  // Always null on this topic.
  name: yup.mixed().nullable().required(),
  total_return_line_items: yup.number().required(),
  // Always empty on this topic.
  return_line_items: yup.array().of(yup.object()).required(),
  return_shipping_fees: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        admin_graphql_api_id: yup.string().required(),
        price: yup
          .object({
            shop_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
            presentment_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
          })
          .required(),
      }),
    )
    .required(),
  exchange_line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        admin_graphql_api_id: yup.string().required(),
        // Always null on this topic.
        line_item: yup.mixed().nullable().required(),
      }),
    )
    .required(),
  total_exchange_line_items: yup.number().required(),
})

// returns/decline webhook schema
export const returnsDeclineSchema = yup.object({
  id: yup.number().required(),
  admin_graphql_api_id: yup.string().required(),
  status: yup.string().required(),
  order: yup
    .object({
      id: yup.number().required(),
      admin_graphql_api_id: yup.string().required(),
    })
    .required(),
  decline: yup
    .object({
      reason: yup.string().required(),
      note: yup.string().nullable().optional(),
    })
    .required(),
})

// returns/cancel webhook schema
export const returnsCancelSchema = yup.object({
  id: yup.number().required(),
  admin_graphql_api_id: yup.string().required(),
  order_id: yup.number().required(),
  status: yup.string().required(),
})

// returns/reopen webhook schema
export const returnsReopenSchema = yup.object({
  id: yup.number().required(),
  admin_graphql_api_id: yup.string().required(),
  order_id: yup.number().required(),
  status: yup.string().required(),
})

export const refundsCreateSchema = yup.object({
  id: yup.number().required(),
  order_id: yup.number().required(),
  created_at: yup.string().required(),
  note: yup.string().nullable().notRequired(),
  user_id: yup.number().nullable().notRequired(),
  processed_at: yup.string().nullable().notRequired(),
  duties: yup.array().of(yup.object().strict()).nullable().notRequired(),
  total_duties_set: yup
    .object({
      shop_money: yup
        .object({
          amount: yup.string().nullable().notRequired(),
          currency_code: yup.string().nullable().notRequired(),
        })
        .strict()
        .nullable()
        .notRequired(),
      presentment_money: yup
        .object({
          amount: yup.string().nullable().notRequired(),
          currency_code: yup.string().nullable().notRequired(),
        })
        .strict()
        .nullable()
        .notRequired(),
    })
    .strict()
    .nullable()
    .notRequired(),
  return: yup.mixed().nullable().notRequired(),
  restock: yup.boolean().nullable().notRequired(),
  refund_shipping_lines: yup.array().of(yup.object().strict()).nullable().notRequired(),
  admin_graphql_api_id: yup.string().required(),
  order_adjustments: yup.array().of(yup.object().strict()).nullable().notRequired(),
  refund_line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        line_item_id: yup.number().required(),
        quantity: yup.number().required(),
        restock_type: yup.string().nullable().notRequired(),
        location_id: yup.number().nullable().notRequired(),
        subtotal: yup.number().nullable().notRequired(),
        total_tax: yup.number().nullable().notRequired(),
        subtotal_set: yup
          .object({
            shop_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency_code: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            presentment_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency_code: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
          })
          .strict()
          .nullable()
          .notRequired(),
        total_tax_set: yup
          .object({
            shop_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency_code: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            presentment_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency_code: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
          })
          .strict()
          .nullable()
          .notRequired(),
        line_item: yup
          .object({
            id: yup.number().required(),
            variant_id: yup.number().nullable().notRequired(),
            title: yup.string().nullable().notRequired(),
            quantity: yup.number().nullable().notRequired(),
            sku: yup.string().nullable().notRequired(),
            variant_title: yup.string().nullable().notRequired(),
            vendor: yup.string().nullable().notRequired(),
            fulfillment_service: yup.string().nullable().notRequired(),
            product_id: yup.number().nullable().notRequired(),
            requires_shipping: yup.boolean().nullable().notRequired(),
            taxable: yup.boolean().nullable().notRequired(),
            gift_card: yup.boolean().nullable().notRequired(),
            name: yup.string().nullable().notRequired(),
            variant_inventory_management: yup.string().nullable().notRequired(),
            properties: yup.array().of(yup.object().strict()).nullable().notRequired(),
            product_exists: yup.boolean().nullable().notRequired(),
            fulfillable_quantity: yup.number().nullable().notRequired(),
            grams: yup.number().nullable().notRequired(),
            price: yup.string().nullable().notRequired(),
            total_discount: yup.string().nullable().notRequired(),
            fulfillment_status: yup.string().nullable().notRequired(),
            price_set: yup
              .object({
                shop_money: yup
                  .object({
                    amount: yup.string().nullable().notRequired(),
                    currency_code: yup.string().nullable().notRequired(),
                  })
                  .strict()
                  .nullable()
                  .notRequired(),
                presentment_money: yup
                  .object({
                    amount: yup.string().nullable().notRequired(),
                    currency_code: yup.string().nullable().notRequired(),
                  })
                  .strict()
                  .nullable()
                  .notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            total_discount_set: yup
              .object({
                shop_money: yup
                  .object({
                    amount: yup.string().nullable().notRequired(),
                    currency_code: yup.string().nullable().notRequired(),
                  })
                  .strict()
                  .nullable()
                  .notRequired(),
                presentment_money: yup
                  .object({
                    amount: yup.string().nullable().notRequired(),
                    currency_code: yup.string().nullable().notRequired(),
                  })
                  .strict()
                  .nullable()
                  .notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            discount_allocations: yup
              .array()
              .of(
                yup
                  .object({
                    amount: yup.string().nullable().notRequired(),
                    discount_application_index: yup.number().nullable().notRequired(),
                    amount_set: yup
                      .object({
                        shop_money: yup
                          .object({
                            amount: yup.string().nullable().notRequired(),
                            currency_code: yup.string().nullable().notRequired(),
                          })
                          .strict()
                          .nullable()
                          .notRequired(),
                        presentment_money: yup
                          .object({
                            amount: yup.string().nullable().notRequired(),
                            currency_code: yup.string().nullable().notRequired(),
                          })
                          .strict()
                          .nullable()
                          .notRequired(),
                      })
                      .strict()
                      .nullable()
                      .notRequired(),
                  })
                  .strict(),
              )
              .nullable()
              .notRequired(),
            origin_location: yup
              .object({
                id: yup.number().nullable().notRequired(),
                country_code: yup.string().nullable().notRequired(),
                province_code: yup.string().nullable().notRequired(),
                name: yup.string().nullable().notRequired(),
                address1: yup.string().nullable().notRequired(),
                address2: yup.string().nullable().notRequired(),
                city: yup.string().nullable().notRequired(),
                zip: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            duties: yup.array().of(yup.object().strict()).nullable().notRequired(),
            admin_graphql_api_id: yup.string().nullable().notRequired(),
            tax_lines: yup.array().of(yup.object().strict()).nullable().notRequired(),
          })
          .nullable()
          .notRequired(),
      }),
    )
    .nullable()
    .notRequired(),
  transactions: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        order_id: yup.number().required(),
        kind: yup.string().nullable().notRequired(),
        gateway: yup.string().nullable().notRequired(),
        status: yup.string().nullable().notRequired(),
        message: yup.string().nullable().notRequired(),
        created_at: yup.string().nullable().notRequired(),
        test: yup.boolean().nullable().notRequired(),
        authorization: yup.string().nullable().notRequired(),
        location_id: yup.number().nullable().notRequired(),
        user_id: yup.number().nullable().notRequired(),
        parent_id: yup.number().nullable().notRequired(),
        processed_at: yup.string().nullable().notRequired(),
        device_id: yup.number().nullable().notRequired(),
        error_code: yup.string().nullable().notRequired(),
        source_name: yup.string().nullable().notRequired(),
        receipt: yup.object().nullable().notRequired(),
        amount: yup.string().nullable().notRequired(),
        currency: yup.string().nullable().notRequired(),
        payment_id: yup.string().nullable().notRequired(),
        total_unsettled_set: yup
          .object({
            presentment_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
            shop_money: yup
              .object({
                amount: yup.string().nullable().notRequired(),
                currency: yup.string().nullable().notRequired(),
              })
              .strict()
              .nullable()
              .notRequired(),
          })
          .strict()
          .nullable()
          .notRequired(),
        manual_payment_gateway: yup.boolean().nullable().notRequired(),
        amount_rounding: yup.string().nullable().notRequired(),
        admin_graphql_api_id: yup.string().nullable().notRequired(),
      }),
    )
    .nullable()
    .notRequired(),
})

export const fulfillmentsUpdateSchema = yup.object({
  id: yup.number().required(),
  order_id: yup.number().required(),
  status: yup.string().required(),
  created_at: yup.string().required(),
  service: yup.string().nullable().notRequired(),
  updated_at: yup.string().required(),
  tracking_company: yup.string().nullable().notRequired(),
  shipment_status: yup.string().nullable().notRequired(),
  location_id: yup.number().nullable().notRequired(),
  origin_address: yup.mixed().nullable().notRequired(),
  email: yup.string().email().notRequired(),
  destination: yup
    .object({
      first_name: yup.string().notRequired(),
      address1: yup.string().notRequired(),
      phone: yup.string().nullable().notRequired(),
      city: yup.string().notRequired(),
      zip: yup.string().notRequired(),
      province: yup.string().notRequired(),
      country: yup.string().notRequired(),
      last_name: yup.string().notRequired(),
      address2: yup.string().nullable().notRequired(),
      company: yup.string().nullable().notRequired(),
      latitude: yup.mixed().nullable().notRequired(),
      longitude: yup.mixed().nullable().notRequired(),
      name: yup.string().notRequired(),
      country_code: yup.string().notRequired(),
      province_code: yup.string().notRequired(),
    })
    .notRequired(),
  line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        variant_id: yup.mixed().nullable().notRequired(),
        title: yup.string().required(),
        quantity: yup.number().required(),
        sku: yup.string().notRequired(),
        variant_title: yup.string().nullable().notRequired(),
        vendor: yup.mixed().nullable().notRequired(),
        fulfillment_service: yup.string().required(),
        product_id: yup.number().notRequired(),
        requires_shipping: yup.boolean().required(),
        taxable: yup.boolean().required(),
        gift_card: yup.boolean().required(),
        name: yup.string().required(),
        variant_inventory_management: yup.mixed().nullable().notRequired(),
        properties: yup.array().of(yup.object()).required(),
        product_exists: yup.boolean().required(),
        fulfillable_quantity: yup.number().required(),
        grams: yup.number().required(),
        price: yup.string().required(),
        total_discount: yup.string().required(),
        fulfillment_status: yup.mixed().nullable().notRequired(),
        price_set: yup
          .object({
            shop_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
            presentment_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
          })
          .required(),
        total_discount_set: yup
          .object({
            shop_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
            presentment_money: yup
              .object({
                amount: yup.string().required(),
                currency_code: yup.string().required(),
              })
              .required(),
          })
          .required(),
        discount_allocations: yup
          .array()
          .of(
            yup.object({
              amount: yup.string().required(),
              discount_application_index: yup.number().required(),
              amount_set: yup
                .object({
                  shop_money: yup
                    .object({
                      amount: yup.string().required(),
                      currency_code: yup.string().required(),
                    })
                    .required(),
                  presentment_money: yup
                    .object({
                      amount: yup.string().required(),
                      currency_code: yup.string().required(),
                    })
                    .required(),
                })
                .required(),
            }),
          )
          .required(),
        duties: yup.array().of(yup.object()).required(),
        admin_graphql_api_id: yup.string().required(),
        tax_lines: yup.array().of(yup.object()).required(),
      }),
    )
    .required(),
  tracking_number: yup.string().nullable().notRequired(),
  tracking_numbers: yup.array().of(yup.string().required()).notRequired(),
  tracking_url: yup.string().nullable().notRequired(),
  tracking_urls: yup.array().of(yup.string().required()).notRequired(),
  receipt: yup.object().required(),
  name: yup.string().required(),
  admin_graphql_api_id: yup.string().required(),
})

/** Same resource shape as fulfillments/update; see Shopify webhook Fulfillment payload. */
export const fulfillmentsCreateSchema = fulfillmentsUpdateSchema

/**
 * Thin fulfillment-order pointer used by most FO webhook payloads.
 * Workflows should Read Fulfillment Order by `id` for full GraphQL fields.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
const fulfillmentOrderWebhookPointerSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
})

/**
 * fulfillment_orders/order_routing_complete
 *
 * Occurs when an order has finished being routed and its fulfillment orders are assigned.
 * Payload is a thin pointer — workflows should Read Fulfillment Order by `fulfillment_order.id`.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersOrderRoutingCompleteSchema = yup.object({
  fulfillment_order: fulfillmentOrderWebhookPointerSchema.required(),
})

/**
 * fulfillment_orders/split
 *
 * Occurs when a fulfillment order is split into multiple fulfillment orders.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersSplitSchema = yup.object({
  split_line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        quantity: yup.number().required(),
      }),
    )
    .required(),
  fulfillment_order: fulfillmentOrderWebhookPointerSchema.required(),
  remaining_fulfillment_order: fulfillmentOrderWebhookPointerSchema.required(),
  replacement_fulfillment_order: fulfillmentOrderWebhookPointerSchema.required(),
})

/**
 * fulfillment_orders/moved
 *
 * Occurs whenever the location assigned to fulfill one or more FO line items changes.
 * Use `source_location` for the originally assigned location — `assigned_location_id` on
 * `original_fulfillment_order` may already reflect the move.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersMovedSchema = yup.object({
  original_fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      assigned_location_id: yup.string().required(),
    })
    .required(),
  moved_fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      assigned_location_id: yup.string().required(),
    })
    .required(),
  destination_location_id: yup.string().required(),
  fulfillment_order_line_items_requested: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        quantity: yup.number().required(),
      }),
    )
    .required(),
  source_location: yup
    .object({
      id: yup.string().required(),
    })
    .required(),
})

/**
 * fulfillment_orders/cancelled
 *
 * Occurs when a fulfillment order is cancelled. Replacement FO may be omitted when there is no
 * remaining work to re-create.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersCancelledSchema = yup.object({
  fulfillment_order: fulfillmentOrderWebhookPointerSchema.required(),
  replacement_fulfillment_order: fulfillmentOrderWebhookPointerSchema.nullable().optional(),
})

const fulfillmentHoldWebhookSchema = yup.object({
  id: yup.string().required(),
  reason: yup.string().required(),
  reason_notes: yup.string().nullable().optional(),
  held_by_requesting_app: yup.boolean().required(),
  handle: yup.string().nullable().optional(),
  held_by_app: yup
    .object({
      id: yup.string().required(),
    })
    .nullable()
    .optional(),
})

/**
 * fulfillment_orders/placed_on_hold
 *
 * Occurs when a fulfillment order transitions to ON_HOLD. Only fires once when the first hold
 * is applied (status changes to ON_HOLD), even if multiple holds are stacked.
 * `remaining_fulfillment_order` / `held_fulfillment_order_line_items` are present for partial
 * holds and may be omitted when the entire FO is held.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersPlacedOnHoldSchema = yup.object({
  fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      fulfillment_holds: yup.array().of(fulfillmentHoldWebhookSchema).required(),
    })
    .required(),
  remaining_fulfillment_order: fulfillmentOrderWebhookPointerSchema.nullable().optional(),
  held_fulfillment_order_line_items: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        quantity: yup.number().required(),
      }),
    )
    .nullable()
    .optional(),
  created_fulfillment_hold: fulfillmentHoldWebhookSchema.required(),
})

const fulfillmentOrderMerchantRequestWebhookSchema = yup.object({
  id: yup.string().required(),
  message: yup.string().nullable().optional(),
})

/**
 * fulfillment_orders/cancellation_request_submitted
 *
 * Occurs when a merchant submits a cancellation request for a fulfillment order to the
 * fulfillment service. The fulfillment service must accept or reject the request.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersCancellationRequestSubmittedSchema = yup.object({
  fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      request_status: yup.string().required(),
    })
    .required(),
  fulfillment_order_merchant_request: fulfillmentOrderMerchantRequestWebhookSchema.required(),
})

/**
 * fulfillment_orders/cancellation_request_accepted
 *
 * Occurs when the fulfillment service accepts a cancellation request. The fulfillment order
 * transitions to CLOSED — re-read the fulfillment order by `fulfillment_order.id` for full state.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersCancellationRequestAcceptedSchema = yup.object({
  fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      request_status: yup.string().nullable().optional(),
    })
    .required(),
  message: yup.string().nullable().optional(),
})

/**
 * fulfillment_orders/cancellation_request_rejected
 *
 * Occurs when the fulfillment service rejects a cancellation request. The fulfillment order
 * retains its prior status and `request_status` reflects the rejection.
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 */
export const fulfillmentOrdersCancellationRequestRejectedSchema = yup.object({
  fulfillment_order: yup
    .object({
      id: yup.string().required(),
      status: yup.string().required(),
      request_status: yup.string().nullable().optional(),
    })
    .required(),
  message: yup.string().nullable().optional(),
})

/**
 * fulfillment_events/create
 *
 * Occurs when a fulfillment (shipment) event is created — a milestone in the shipment lifecycle
 * from creation through delivery (e.g. in_transit, out_for_delivery, delivered).
 *
 * @see https://shopify.dev/docs/api/webhooks/latest
 * @see https://shopify.dev/docs/api/admin-graphql/latest/objects/FulfillmentEvent
 */
export const fulfillmentEventsCreateSchema = yup.object({
  id: yup.number().required(),
  fulfillment_id: yup.number().required(),
  order_id: yup.number().required(),
  status: yup.string().required(),
  message: yup.string().nullable().optional(),
  happened_at: yup.string().required(),
  city: yup.string().nullable().optional(),
  province: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  zip: yup.string().nullable().optional(),
  address1: yup.string().nullable().optional(),
  latitude: yup.number().nullable().optional(),
  longitude: yup.number().nullable().optional(),
  shop_id: yup.number().nullable().optional(),
  created_at: yup.string().required(),
  updated_at: yup.string().required(),
  estimated_delivery_at: yup.string().nullable().optional(),
  admin_graphql_api_id: yup.string().required(),
})

// SHOPIFY FULFILLMENT ORDER MOVE SCHEMA -------------------------------------------------------------

export const shopifyFulfillmentOrderMoveResponse = yup.object({
  data: yup.object({
    fulfillmentOrderMove: yup.object({
      movedFulfillmentOrder: yup.object({
        id: yup.string().required(),
        status: yup.string().optional().nullable(),
        requestStatus: yup.string().optional().nullable(),
        assignedLocation: yup
          .object({
            location: yup
              .object({
                id: yup.string().optional().nullable(),
                name: yup.string().optional().nullable(),
              })
              .optional()
              .nullable(),
          })
          .optional()
          .nullable(),
      }),
      originalFulfillmentOrder: yup.object({
        id: yup.string().required(),
        status: yup.string().optional().nullable(),
      }),
      userErrors: yup
        .array()
        .of(
          yup.object({
            field: yup.array().of(yup.string()).optional().nullable(),
            message: yup.string().required(),
          }),
        )
        .default([])
        .required(),
    }),
  }),
})

export type ShopifyFulfillmentOrderMoveResponse = yup.InferType<
  typeof shopifyFulfillmentOrderMoveResponse
>

// DRAFT ORDER MUTATIONS -----------------------------------------------------------------------------

const shopifyDraftOrderUserErrorSchema = yup.object({
  field: yup.array().of(yup.string()).nullable().notRequired(),
  message: yup.string().required(),
})

/** https://shopify.dev/docs/api/admin-graphql/latest/mutations/draftOrderCreate */
export const shopifyDraftOrderCreateResponse = yup.object({
  data: yup.object({
    draftOrderCreate: yup.object({
      draftOrder: yup
        .object({
          id: yup.string().required(),
          name: yup.string().notRequired(),
          status: yup.string().notRequired(),
          email: yup.string().nullable().notRequired(),
          createdAt: yup.string().notRequired(),
          updatedAt: yup.string().notRequired(),
          totalPriceSet: yup
            .object({
              shopMoney: yup
                .object({
                  amount: yup.string().required(),
                  currencyCode: yup.string().required(),
                })
                .required(),
              presentmentMoney: yup
                .object({
                  amount: yup.string().required(),
                  currencyCode: yup.string().required(),
                })
                .required(),
            })
            .nullable()
            .notRequired(),
        })
        .nullable()
        .notRequired(),
      userErrors: yup.array().of(shopifyDraftOrderUserErrorSchema).nullable().notRequired(),
    }),
  }),
})

export type ShopifyDraftOrderCreateResponse = yup.InferType<typeof shopifyDraftOrderCreateResponse>

/** https://shopify.dev/docs/api/admin-graphql/latest/mutations/draftOrderUpdate */
export const shopifyDraftOrderUpdateResponse = yup.object({
  data: yup.object({
    draftOrderUpdate: yup.object({
      draftOrder: yup
        .object({
          id: yup.string().required(),
          name: yup.string().notRequired(),
          status: yup.string().notRequired(),
          email: yup.string().nullable().notRequired(),
          createdAt: yup.string().notRequired(),
          updatedAt: yup.string().notRequired(),
          totalPriceSet: yup
            .object({
              shopMoney: yup
                .object({
                  amount: yup.string().required(),
                  currencyCode: yup.string().required(),
                })
                .required(),
              presentmentMoney: yup
                .object({
                  amount: yup.string().required(),
                  currencyCode: yup.string().required(),
                })
                .required(),
            })
            .nullable()
            .notRequired(),
        })
        .nullable()
        .notRequired(),
      userErrors: yup.array().of(shopifyDraftOrderUserErrorSchema).nullable().notRequired(),
    }),
  }),
})

export type ShopifyDraftOrderUpdateResponse = yup.InferType<typeof shopifyDraftOrderUpdateResponse>

/** https://shopify.dev/docs/api/admin-graphql/latest/mutations/draftOrderDelete */
export const shopifyDraftOrderDeleteResponse = yup.object({
  data: yup.object({
    draftOrderDelete: yup.object({
      deletedId: yup.string().nullable().notRequired(),
      userErrors: yup.array().of(shopifyDraftOrderUserErrorSchema).nullable().notRequired(),
    }),
  }),
})

export type ShopifyDraftOrderDeleteResponse = yup.InferType<typeof shopifyDraftOrderDeleteResponse>
